import _ from 'lodash';

import {
  GAME_ID,
  REDUX_ACTIONS,
  NARRATION_TYPES,
  NARRATION_HINT_TYPES,
  NARRATION_TIP_TYPES,
} from '../../data/wordCityConstants';

const initialState = {
  narrationId: null,
  narrationType: '',
  // what plays on its own when the narration changes
  narrations: [],
  // what plays when the child taps Alex, which always includes the step's instruction
  replayNarrations: [],
  // the step's own instruction, kept aside so a hint can lead with it
  tipNarrations: [],
};

const getStandardHintNarrations = ({ hintType, phonemes }) => {
  const [phoneme1, phoneme2] = phonemes;

  switch (hintType) {
    // single vo
    case NARRATION_HINT_TYPES.LOOK_BEGINNING:
      return [{
        section: 'vo',
        key: 'lookBeginning',
      }];
    case NARRATION_HINT_TYPES.LOOK_MIDDLE:
      return [{
        section: 'vo',
        key: 'lookMiddle',
      }];
    case NARRATION_HINT_TYPES.LOOK_MIDDLES:
      return [{
        section: 'vo',
        key: 'lookMiddles',
      }];
    case NARRATION_HINT_TYPES.LOOK_ENDING:
      return [{
        section: 'vo',
        key: 'lookEnding',
      }];
    case NARRATION_HINT_TYPES.LOOK_ENDINGS:
      return [{
        section: 'vo',
        key: 'lookEndings',
      }];
    case NARRATION_HINT_TYPES.REMOVE_BEGINNING:
      return [{
        section: 'vo',
        key: 'removeBeginning',
      }];
    case NARRATION_HINT_TYPES.REMOVE_ENDING:
      return [{
        section: 'vo',
        key: 'removeEnding',
      }];
    case NARRATION_HINT_TYPES.ADD_MAGIC:
      return [{
        section: 'vo',
        key: 'addMagic',
      }];
    // vo/phoneme
    case NARRATION_HINT_TYPES.CHANGE_BEGINNING:
      return [{
        section: 'vo',
        key: 'changeBeginning',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    case NARRATION_HINT_TYPES.CHANGE_MIDDLE:
      return [{
        section: 'vo',
        key: 'changeMiddle',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    case NARRATION_HINT_TYPES.CHANGE_MIDDLES:
      return [{
        section: 'vo',
        key: 'changeMiddle',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    case NARRATION_HINT_TYPES.CHANGE_ENDING:
      return [{
        section: 'vo',
        key: 'changeEnding',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    case NARRATION_HINT_TYPES.CHANGE_ENDINGS:
      return [{
        section: 'vo',
        key: 'changeEndings',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    // vo/phoneme/sound
    case NARRATION_HINT_TYPES.CHANGE_SOUND:
      return [{
        section: 'vo',
        key: 'changeThe',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }, {
        section: 'vo',
        key: 'sound',
      }];
    case NARRATION_HINT_TYPES.REMOVE_SOUND:
      return [{
        section: 'vo',
        key: 'removeThe',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }, {
        section: 'vo',
        key: 'sound',
      }];
    case NARRATION_HINT_TYPES.ADD_SOUND:
      return [{
        section: 'vo',
        key: 'addThe',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }, {
        section: 'vo',
        key: 'sound',
      }];
    case NARRATION_HINT_TYPES.ADD_BEGINNING:
      return [{
        section: 'vo',
        key: 'addBeginning',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    case NARRATION_HINT_TYPES.ADD_ENDING:
      return [{
        section: 'vo',
        key: 'addEnding',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }];
    // vo/phoneme/vo
    case NARRATION_HINT_TYPES.SOUND_BEGINNING:
      return [{
        section: 'vo',
        key: 'addThe',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }, {
        section: 'vo',
        key: 'soundBeginning',
      }];
    // vo/phoneme/vo/vo/phoneme
    case NARRATION_HINT_TYPES.CHANGE_SOUND_TO:
      return [{
        section: 'vo',
        key: 'changeThe',
      }, {
        section: 'phonemes',
        key: phoneme1,
      }, {
        section: 'vo',
        key: 'sound',
      }, {
        section: 'vo',
        key: 'to',
      }, {
        section: 'phonemes',
        key: phoneme2,
      }];
    default:
      return [];
  }
};

const getStandardTipNarrations = ({ tipType, referenceWord, targetWord }) => {
  switch (tipType) {
    case NARRATION_TIP_TYPES.ONBOARDING_TIP_DRAG_THE_LETTER:
      return [{
        section: 'vo',
        key: 'onboardingDrag',
      }, {
        section: 'words',
        key: targetWord,
      }];
    case NARRATION_TIP_TYPES.STANDARD_TIP_CHANGE:
      return [{
        section: 'vo',
        key: 'change',
      }, {
        section: 'words',
        key: referenceWord,
      }, {
        section: 'vo',
        key: 'to',
      }, {
        section: 'words',
        key: targetWord,
      }];
    case NARRATION_TIP_TYPES.ONBOARDING_TIP_REMOVE:
      return [{
        section: 'vo',
        key: 'onboardingRemove',
      }, {
        section: 'vo',
        key: 'change',
      }, {
        section: 'words',
        key: referenceWord,
      }, {
        section: 'vo',
        key: 'to',
      }, {
        section: 'words',
        key: targetWord,
      }];
    default:
      return [];
  }
};

const wordCityNarrationReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    // so a skipped tip leaves Alex nothing to replay
    case REDUX_ACTIONS.CLEAR_NARRATION: {
      return initialState;
    }
    case REDUX_ACTIONS.INIT_STANDARD_TIP_NARRATION: {
      const tipNarrations = getStandardTipNarrations(action.payload);

      return {
        ...initialState,
        narrationId: _.uniqueId('narration_'),
        narrationType: NARRATION_TYPES.STANDARD_TIP,
        narrations: tipNarrations,
        replayNarrations: tipNarrations,
        tipNarrations,
      };
    }
    case REDUX_ACTIONS.INIT_STANDARD_HINT_NARRATION: {
      const { isFirstHint } = action.payload;
      const hintNarrations = getStandardHintNarrations(action.payload);
      /* The first hint only nudges, so it closes on the instruction and leaves the child holding the
      task. The last one gives the answer, so the instruction leads and the answer is what they are
      left with. */
      const withInstruction = isFirstHint
        ? [...hintNarrations, ...state.tipNarrations]
        : [...state.tipNarrations, ...hintNarrations];

      return {
        ...initialState,
        narrationId: _.uniqueId('narration_'),
        narrationType: NARRATION_TYPES.STANDARD_HINT,
        // the last hint autoplays alone; restating the whole task around the answer would bury it
        narrations: isFirstHint ? withInstruction : hintNarrations,
        replayNarrations: withInstruction,
        tipNarrations: state.tipNarrations,
      };
    }
    default:
      return state;
  }
};

export default wordCityNarrationReducer;
